// popupContext.tsx
import React, {
  ComponentType,
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';

type PopupState = {
  PopupComponent: ComponentType | null;
  popupKey: number | null;
  listensToClickAway: boolean;
};

type PopupActions = {
  showPopup: (Component: ComponentType, listensToClickAway?: boolean) => void;
  hidePopup: () => void;
};

const PopupStateContext = createContext<PopupState | undefined>(undefined);
const PopupActionsContext = createContext<PopupActions | undefined>(undefined);

export const usePopupState = (): PopupState => {
  const ctx = useContext(PopupStateContext);
  if (!ctx) throw new Error('usePopupState must be used within PopupProvider');
  return ctx;
};

export const usePopup = (): PopupActions => {
  const ctx = useContext(PopupActionsContext);
  if (!ctx) throw new Error('usePopupActions must be used within PopupProvider');
  return ctx;
};

export const PopupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [PopupComponent, setPopupComponent] = useState<ComponentType | null>(null);
  const [popupKey, setPopupKey] = useState<number | null>(null);
  const [listensToClickAway, setListensToClickAway] = useState(true);

  const showPopup = useCallback((Component: ComponentType, listens = true) => {
    setPopupKey(Date.now());
    setPopupComponent(() => Component);
    setListensToClickAway(listens);
  }, []);

  const hidePopup = useCallback(() => {
    setPopupComponent(null);
    setPopupKey(null);
    setListensToClickAway(true);
  }, []);

  const actions = useMemo(() => ({ showPopup, hidePopup }), [showPopup, hidePopup]);

  return (
    <PopupActionsContext.Provider value={actions}>
      <PopupStateContext.Provider value={{ PopupComponent, popupKey, listensToClickAway }}>
        {children}
      </PopupStateContext.Provider>
    </PopupActionsContext.Provider>
  );
};
